// src/pages/Orders/OrderForm/OrderFormSections.jsx
import React, { useState } from "react";
import Button from "../../../../components/common/Button";
import Input from "../../../../components/common/Input";
import Badge from "../../../../components/common/Badge";
import {
  UserIcon,
  ShoppingBagIcon,
  RefreshIcon,
  LocationMarkerIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  SearchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserAddIcon,
  PhoneIcon,
  MailIcon,
  XCircleIcon,
  CheckCircleIcon,
  CubeIcon,
  PlusCircleIcon,
  SparklesIcon,
  ScaleIcon,
  TagIcon,
  MinusCircleIcon,
  CalendarIcon,
} from "@heroicons/react/outline";

export { default as SessionSelectionSection } from "./SessionSelectionSection";
export { default as EnhancedExchangeSection } from "./EnhancedExchangeSection";
export { default as EnhancedPricingSummarySection } from "./EnhancedPricingSummarySection";

// ============= CUSTOMER SELECTION SECTION =============
export const CustomerSelectionSection = ({
  formData,
  errors,
  selectedUser,
  onSelectUser,
  onDeselectUser,
  onChange,
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UserIcon className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Customer Information
          </h2>
        </div>
        {selectedUser && (
          <Badge variant="success" size="sm">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Customer Selected
          </Badge>
        )}
      </div>

      {/* Show selected user */}
      {selectedUser ? (
        <div className="space-y-4">
          {/* Selected User Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedUser.name}
                  </h3>
                  {selectedUser.accountType && (
                    <Badge
                      variant={
                        selectedUser.accountType === "B2B"
                          ? "primary"
                          : "secondary"
                      }
                      size="sm"
                    >
                      {selectedUser.accountType}
                    </Badge>
                  )}
                  {selectedUser.jewelleryProfile?.kycVerified && (
                    <Badge variant="success" size="sm">
                      KYC Verified
                    </Badge>
                  )}
                </div>

                <div className="space-y-1">
                  {selectedUser.phoneNumber && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <PhoneIcon className="w-4 h-4" />
                      <span>{selectedUser.phoneNumber}</span>
                    </div>
                  )}
                  {selectedUser.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MailIcon className="w-4 h-4" />
                      <span>{selectedUser.email}</span>
                    </div>
                  )}
                  {selectedUser.gstin && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-medium">GSTIN:</span>
                      <span>{selectedUser.gstin}</span>
                    </div>
                  )}
                </div>

                {/* Show address if available */}
                {selectedUser.address &&
                  (selectedUser.address.line1 || selectedUser.address.city) && (
                    <div className="mt-2 text-sm text-gray-600">
                      <span className="font-medium">Address: </span>
                      {[
                        selectedUser.address.line1,
                        selectedUser.address.line2,
                        selectedUser.address.city,
                        selectedUser.address.state,
                        selectedUser.address.pinCode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  )}
              </div>

              {/* Deselect Button */}
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={onDeselectUser}
                className="ml-4"
              >
                <XCircleIcon className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>

          {/* Option to change customer */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Need to select a different customer?
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onSelectUser}
            >
              <UserAddIcon className="w-4 h-4 mr-1" />
              Select Another Customer
            </Button>
          </div>
        </div>
      ) : (
        /* Show customer selection/input */
        <div className="space-y-4">
          {/* Select Existing Customer Button */}
          <div className="flex items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-gray-50 transition-all">
            <Button
              type="button"
              variant="primary"
              onClick={onSelectUser}
              className="flex items-center gap-2"
            >
              <UserAddIcon className="w-5 h-5" />
              Select Existing Customer
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or enter guest customer details
              </span>
            </div>
          </div>

          {/* Guest Customer Input Fields */}
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-3">
              Enter details for a guest customer (without creating an account)
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Guest Name"
                name="guestCustomer.name"
                value={formData.guestCustomer?.name || ""}
                onChange={onChange}
                error={errors["guestCustomer.name"]}
                placeholder="Enter customer name"
                required={!selectedUser}
              />
              <Input
                label="Guest Phone"
                name="guestCustomer.phone"
                type="tel"
                value={formData.guestCustomer?.phone || ""}
                onChange={onChange}
                maxLength="10"
                error={errors["guestCustomer.phone"]}
                placeholder="10-digit phone number"
                required={!selectedUser}
              />
              <div className="sm:col-span-2">
                <Input
                  label="Guest Email (Optional)"
                  name="guestCustomer.email"
                  type="email"
                  value={formData.guestCustomer?.email || ""}
                  onChange={onChange}
                  error={errors["guestCustomer.email"]}
                  placeholder="customer@example.com"
                />
              </div>
            </div>
          </div>

          {/* Show error if no customer selected */}
          {errors.customer && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{errors.customer}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============= ITEMS SECTION =============
export const ItemsSection = ({
  items = [],
  errors,
  onAddItem,
  onUpdateQuantity,
  onRemoveItem,
  onShowProductSelection,
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShoppingBagIcon className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Order Items</h2>
          {items.length > 0 && (
            <Badge variant="primary" size="sm">
              {items.length} item{items.length !== 1 ? "s" : ""}
            </Badge>
          )}
        </div>
      </div>

      {/* Items List */}
      {items.length > 0 ? (
        <div className="space-y-3 mb-4">
          {items.map((item, index) => (
            <ProductItemCard
              key={item.productId || item.product?._id || index}
              item={item}
              onUpdateQuantity={(newQty) =>
                onUpdateQuantity(item.productId || item.product?._id, newQty)
              }
              onRemove={() => onRemoveItem(item.productId || item.product?._id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg mb-4">
          <CubeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No items added yet</p>
          <p className="text-sm text-gray-400">
            Click "Add Product" to start building your order
          </p>
        </div>
      )}

      {/* Error Display */}
      {errors?.items && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errors.items}</p>
        </div>
      )}

      {/* Add Product Button */}
      <Button
        type="button"
        variant="primary"
        onClick={onShowProductSelection}
        className="w-full"
      >
        <PlusCircleIcon className="w-5 h-5 mr-2" />
        Add Product
      </Button>
    </div>
  );
};

// Product Item Card Component
const ProductItemCard = ({ item, onUpdateQuantity, onRemove }) => {
  const product = item.product || item.productSnapshot || {};
  const quantity = item.quantity || 1;

  const handleIncrement = () => {
    onUpdateQuantity(quantity + 1);
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      onUpdateQuantity(quantity - 1);
    }
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    if (value > 0) {
      onUpdateQuantity(value);
    }
  };

  // Calculate item total
  const itemPrice =
    item.pricingSnapshot?.totalPrice || product.pricing?.sellingPrice || 0;
  const itemTotal = itemPrice * quantity;

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="flex-shrink-0 w-20 h-20 bg-gray-100 rounded-lg overflow-hidden">
          {product.media?.[0]?.url ? (
            <img
              src={product.media[0].url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <CubeIcon className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">
                {product.name || "Unknown Product"}
              </h3>
              <p className="text-sm text-gray-500">
                ID: {product.productId || product._id || "N/A"}
              </p>
            </div>

            {/* Remove Button */}
            <button
              type="button"
              onClick={onRemove}
              className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
              title="Remove item"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Product Badges */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {product.type && (
              <Badge variant="secondary" size="sm">
                {product.type}
              </Badge>
            )}
            {product.subType && (
              <Badge variant="secondary" size="sm">
                {product.subType}
              </Badge>
            )}
            {product.purity && (
              <Badge variant="info" size="sm">
                <SparklesIcon className="w-3 h-3 mr-1" />
                {product.purity}
              </Badge>
            )}
            {product.hallmark?.isHallmarked && (
              <Badge variant="success" size="sm">
                Hallmarked
              </Badge>
            )}
          </div>

          {/* Product Specs */}
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
            <div className="flex items-center gap-1">
              <ScaleIcon className="w-3 h-3" />
              <span className="font-medium">Gross:</span>{" "}
              {product.grossWeight || 0}g
            </div>
            <div className="flex items-center gap-1">
              <ScaleIcon className="w-3 h-3" />
              <span className="font-medium">Net:</span> {product.netWeight || 0}
              g
            </div>
            {product.metalColor && (
              <div className="flex items-center gap-1">
                <SparklesIcon className="w-3 h-3" />
                <span className="font-medium">Color:</span> {product.metalColor}
              </div>
            )}
            {product.karigarCode && (
              <div className="flex items-center gap-1">
                <TagIcon className="w-3 h-3" />
                <span className="font-medium">Karigar:</span>{" "}
                {product.karigarCode}
              </div>
            )}
          </div>

          {/* Quantity Control & Price */}
          <div className="flex items-center justify-between">
            {/* Quantity Controls */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Qty:</span>
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  type="button"
                  onClick={handleDecrement}
                  className="px-2 py-1 hover:bg-gray-100 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={quantity <= 1}
                >
                  <MinusCircleIcon className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={handleQuantityChange}
                  min="1"
                  className="w-16 text-center border-x border-gray-300 py-1 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleIncrement}
                  className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                >
                  <PlusCircleIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Price */}
            <div className="text-right">
              {itemPrice > 0 && (
                <>
                  <p className="text-xs text-gray-500">
                    {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: "INR",
                      maximumFractionDigits: 0,
                    }).format(itemPrice)}{" "}
                    × {quantity}
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: "INR",
                      maximumFractionDigits: 0,
                    }).format(itemTotal)}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Stock Warning */}
          {product.stock?.availableQuantity !== undefined &&
            quantity > product.stock.availableQuantity && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                ⚠️ Only {product.stock.availableQuantity} units available in
                stock
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

// ============= EXCHANGE SECTION =============
export const ExchangeSection = ({
  exchangeItems,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const totalExchangeValue = exchangeItems.reduce(
    (sum, item) => sum + (item.finalValue || 0),
    0
  );

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <RefreshIcon className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">
            Exchange Items ({exchangeItems.length})
          </h3>
          {totalExchangeValue > 0 && (
            <Badge variant="green" size="sm">
              {formatCurrency(totalExchangeValue)}
            </Badge>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          <div className="mt-4 space-y-4">
            {exchangeItems.length > 0 ? (
              <>
                {exchangeItems.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {item.type || "Gold"} Item
                        </h4>
                        <p className="text-sm text-gray-600">
                          Exchange #{index + 1}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            /* Edit logic */
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemoveItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Weight:</span>{" "}
                        <span className="font-medium">{item.weight}g</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Purity:</span>{" "}
                        <span className="font-medium">{item.purity}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Evaluated:</span>{" "}
                        <span className="font-medium">
                          {formatCurrency(item.evaluatedValue)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Deductions:</span>{" "}
                        <span className="font-medium text-red-600">
                          -{formatCurrency(item.deductions)}
                        </span>
                      </div>
                      <div className="sm:col-span-2">
                        <span className="text-gray-600">Final Value:</span>{" "}
                        <span className="font-bold text-green-600">
                          {formatCurrency(item.finalValue)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(true)}
                  className="w-full"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Another Exchange Item
                </Button>

                {/* Total Exchange Value */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">
                      Total Exchange Value
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {formatCurrency(totalExchangeValue)}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <RefreshIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">
                  No exchange items added yet.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(true)}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Exchange Item
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============= FULFILLMENT SECTION - UPDATED =============
// Update FulfillmentSection component with correct fulfillment structure
export const FulfillmentSection = ({
  formData,
  errors,
  branches,
  onChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [enableScheduling, setEnableScheduling] = useState(false);

  // Determine if pickup or delivery for conditional rendering
  const isPickup = formData.fulfillment?.type === "PICKUP";
  const isDelivery = formData.fulfillment?.type === "DELIVERY";

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <LocationMarkerIcon className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">
            Fulfillment Details
          </h3>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          <div className="mt-4 space-y-4">
            {/* Fulfillment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fulfillment Type *
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="fulfillment.type"
                    value="PICKUP"
                    checked={isPickup}
                    onChange={onChange}
                    className="mr-2"
                  />
                  <span>Store Pickup</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="fulfillment.type"
                    value="DELIVERY"
                    checked={isDelivery}
                    onChange={onChange}
                    className="mr-2"
                  />
                  <span>Home Delivery</span>
                </label>
              </div>
            </div>

            {/* Branch Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch *
                </label>
                <select
                  name="fulfillment.location.branchCode"
                  value={formData.fulfillment?.location?.branchCode || ""}
                  onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors["fulfillment.location.branchCode"]
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select Branch</option>
                  {branches?.map((branch) => (
                    <option key={branch.code} value={branch.code}>
                      {branch.name} - {branch.code}
                    </option>
                  ))}
                </select>
                {errors["fulfillment.location.branchCode"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["fulfillment.location.branchCode"]}
                  </p>
                )}
              </div>

              {/* Store Code (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Store Code (Optional)
                </label>
                <input
                  type="text"
                  name="fulfillment.location.storeCode"
                  value={formData.fulfillment?.location?.storeCode || ""}
                  onChange={onChange}
                  placeholder="Store code if applicable"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Scheduling Section */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-gray-600" />
                  Schedule {isPickup ? "Pickup" : "Delivery"}
                </h4>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={enableScheduling}
                    onChange={(e) => setEnableScheduling(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">Schedule now</span>
                </label>
              </div>

              {enableScheduling && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Scheduled Date - Different path for pickup vs delivery */}
                    <Input
                      label="Scheduled Date"
                      type="date"
                      name={
                        isPickup
                          ? "fulfillment.pickup.scheduledDate"
                          : "fulfillment.delivery.scheduledDate"
                      }
                      value={
                        isPickup
                          ? formData.fulfillment?.pickup?.scheduledDate || ""
                          : formData.fulfillment?.delivery?.scheduledDate || ""
                      }
                      onChange={onChange}
                      min={new Date().toISOString().split("T")[0]}
                    />

                    {/* Time Slot - Different path for pickup vs delivery */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Time Slot (Optional)
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="time"
                          name={
                            isPickup
                              ? "fulfillment.pickup.scheduledTimeSlot.start"
                              : "fulfillment.delivery.scheduledTimeSlot.start"
                          }
                          value={
                            isPickup
                              ? formData.fulfillment?.pickup?.scheduledTimeSlot
                                  ?.start || "10:00"
                              : formData.fulfillment?.delivery
                                  ?.scheduledTimeSlot?.start || "10:00"
                          }
                          onChange={onChange}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <input
                          type="time"
                          name={
                            isPickup
                              ? "fulfillment.pickup.scheduledTimeSlot.end"
                              : "fulfillment.delivery.scheduledTimeSlot.end"
                          }
                          value={
                            isPickup
                              ? formData.fulfillment?.pickup?.scheduledTimeSlot
                                  ?.end || "12:00"
                              : formData.fulfillment?.delivery
                                  ?.scheduledTimeSlot?.end || "18:00"
                          }
                          onChange={onChange}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Start time - End time
                      </p>
                    </div>
                  </div>

                  {/* Notes - Different path for pickup vs delivery */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isPickup ? "Pickup" : "Delivery"} Notes (Optional)
                    </label>
                    <textarea
                      name={
                        isPickup
                          ? "fulfillment.pickup.pickupNotes"
                          : "fulfillment.delivery.deliveryNotes"
                      }
                      value={
                        isPickup
                          ? formData.fulfillment?.pickup?.pickupNotes || ""
                          : formData.fulfillment?.delivery?.deliveryNotes || ""
                      }
                      onChange={onChange}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder={`Add any special instructions for ${
                        isPickup ? "pickup" : "delivery"
                      }...`}
                    />
                  </div>

                  {/* Delivery Charges (only for delivery) */}
                  {isDelivery && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Charges (₹)
                      </label>
                      <input
                        type="number"
                        name="fulfillment.delivery.deliveryCharges"
                        value={
                          formData.fulfillment?.delivery?.deliveryCharges || 0
                        }
                        onChange={onChange}
                        min="0"
                        step="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Enter delivery charges"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Delivery Address */}
            {isDelivery && (
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900">
                  Delivery Address *
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <Input
                    label="Address Line 1 *"
                    name="fulfillment.location.address.line1"
                    value={formData.fulfillment?.location?.address?.line1 || ""}
                    onChange={onChange}
                    placeholder="House/Flat No., Building Name"
                    error={errors["fulfillment.location.address.line1"]}
                  />
                  <Input
                    label="Address Line 2"
                    name="fulfillment.location.address.line2"
                    value={formData.fulfillment?.location?.address?.line2 || ""}
                    onChange={onChange}
                    placeholder="Street, Landmark (Optional)"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Input
                      label="City *"
                      name="fulfillment.location.address.city"
                      value={
                        formData.fulfillment?.location?.address?.city || ""
                      }
                      onChange={onChange}
                      placeholder="City name"
                      error={errors["fulfillment.location.address.city"]}
                    />
                    <Input
                      label="State *"
                      name="fulfillment.location.address.state"
                      value={
                        formData.fulfillment?.location?.address?.state || ""
                      }
                      onChange={onChange}
                      placeholder="State name"
                      error={errors["fulfillment.location.address.state"]}
                    />
                    <Input
                      label="Pincode *"
                      name="fulfillment.location.address.pincode"
                      value={
                        formData.fulfillment?.location?.address?.pincode || ""
                      }
                      onChange={onChange}
                      placeholder="6-digit PIN"
                      maxLength="6"
                      error={errors["fulfillment.location.address.pincode"]}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Special Instructions (Common for both) */}
            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Special Instructions (Optional)
              </label>
              <textarea
                name="fulfillment.specialInstructions"
                value={formData.fulfillment?.specialInstructions || ""}
                onChange={onChange}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Any special instructions for the fulfillment team..."
              />
              <p className="text-xs text-gray-500 mt-1">
                These instructions will be visible to the fulfillment team
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= PAYMENT SECTION =============
export const PaymentSection = ({ formData, errors, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <CurrencyRupeeIcon className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">
            Payment Information
          </h3>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method *
              </label>
              <select
                name="payment.method"
                value={formData.payment?.method || "CASH"}
                onChange={onChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors["payment.method"]
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="CASH">Cash</option>
                <option value="CARD">Card</option>
                <option value="UPI">UPI</option>
                <option value="NETBANKING">Net Banking</option>
                <option value="WALLET">Wallet</option>
                <option value="EMI">EMI</option>
              </select>
              {errors["payment.method"] && (
                <p className="mt-1 text-sm text-red-500">
                  {errors["payment.method"]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </label>
              <select
                name="payment.status"
                value={formData.payment?.status || "PENDING"}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="PENDING">Pending</option>
                <option value="PARTIAL">Partial</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= NOTES SECTION =============
export const NotesSection = ({ formData, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <DocumentTextIcon className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">
            Notes (Optional)
          </h3>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes || ""}
                onChange={onChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add any notes about this order..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Notes (Internal)
              </label>
              <textarea
                name="adminNotes"
                value={formData.adminNotes || ""}
                onChange={onChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Internal notes (not visible to customer)..."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= PRICING SUMMARY SECTION =============
export const PricingSummarySection = ({
  pricingSummary,
  calculating,
  onRecalculate,
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (!pricingSummary && !calculating) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Pricing Summary</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRecalculate}
          disabled={calculating}
        >
          <RefreshIcon
            className={`w-4 h-4 mr-2 ${calculating ? "animate-spin" : ""}`}
          />
          {calculating ? "Calculating..." : "Recalculate"}
        </Button>
      </div>

      {calculating ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-600 mt-2">Calculating pricing...</p>
        </div>
      ) : pricingSummary ? (
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Total Items</span>
            <span className="font-medium">
              {pricingSummary.totalItems || 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium">
              {formatCurrency(pricingSummary.subtotal)}
            </span>
          </div>
          {pricingSummary.totalDiscount > 0 && (
            <div className="flex justify-between text-red-600">
              <span>Discount</span>
              <span className="font-medium">
                -{formatCurrency(pricingSummary.totalDiscount)}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">GST</span>
            <span className="font-medium">
              {formatCurrency(pricingSummary.totalGST)}
            </span>
          </div>
          <div className="flex justify-between pt-3 border-t text-lg font-bold">
            <span>Grand Total</span>
            <span className="text-primary">
              {formatCurrency(pricingSummary.grandTotal)}
            </span>
          </div>
          {pricingSummary.totalExchangeValue > 0 && (
            <>
              <div className="flex justify-between text-green-600">
                <span>Exchange Value</span>
                <span className="font-medium">
                  -{formatCurrency(pricingSummary.totalExchangeValue)}
                </span>
              </div>
              <div className="flex justify-between pt-3 border-t text-lg font-bold">
                <span>Amount Due</span>
                <span className="text-red-600">
                  {formatCurrency(pricingSummary.amountDue)}
                </span>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};
